import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ThemingService {
  private THEMING_STORAGE_KEY = 'CT_THEMING';
  themeChange: Subject<string> = new Subject<string>();
  mode: string;
  theme = '';
  primary = '#f8fafc';
  primary1 = '#f1f5f9'
  primary2 = '#e2e8f0';
  primary3 = '#cbd5e1';
  secondary = '#0a242e';
  primaryDark = '#1a1a1a';
  primary1Dark = '#313131';
  primary2Dark = '#484848';
  primary3Dark = '#5f5f5f';
  secondaryDark = '#f8fafc';
  accent = '#0A242E';
  background = '#0A242E';
  lsAvailable = false;

  constructor() {
    try {
      localStorage.setItem('test', 'test');
      localStorage.removeItem('test');
      this.lsAvailable = true;
    } catch (e) {
      this.lsAvailable = false;
    }

    this.mode = this.read() ?? 'light';
    this.setTheme();
    window.matchMedia('(prefers-color-scheme: dark)').onchange = () =>
      this.setTheme();
  }

  toggleTheme() {
    switch (this.mode) {
      case 'auto': {
        this.mode = 'light';
        break;
      }
      case 'light': {
        this.mode = 'dark';
        break;
      }
      case 'dark': {
        this.mode = 'auto';
        break;
      }
    }
    this.setTheme();
  }

  setMode(mode: 'light' | 'dark' | 'auto'): void {
    this.mode = mode;
    this.setTheme();
  }

  private setTheme() {
    switch (this.mode) {
      case 'dark': {
        this.setDarkTheme();
        break;
      }
      case 'light': {
        this.setLightTheme();
        break;
      }
      case 'auto': {
        window.matchMedia('(prefers-color-scheme: dark)').matches
          ? this.setDarkTheme()
          : this.setLightTheme();
        break;
      }
    }
  }

  setLightTheme() {
    document.documentElement.setAttribute('data-theme', 'light');
    document.documentElement.style.setProperty('--primary', this.primary);
    document.documentElement.style.setProperty('--primary-1', this.primary1);
    document.documentElement.style.setProperty('--primary-2', this.primary2);
    document.documentElement.style.setProperty('--primary-3', this.primary3);
    document.documentElement.style.setProperty('--secondary', this.secondary);
    document.documentElement.style.setProperty('--accent-1', this.secondary);
    document.documentElement.style.setProperty('--accent-2', this.secondary);
    this.themeChange.next('light');
    this.theme = 'light';
    this.write();
  }

  setDarkTheme() {
    document.documentElement.setAttribute('data-theme', 'dark');
    document.documentElement.style.setProperty('--primary', this.primaryDark);
    document.documentElement.style.setProperty(
      '--primary-1',
      this.primary1Dark
    );
    document.documentElement.style.setProperty(
      '--primary-2',
      this.primary2Dark
    );
    document.documentElement.style.setProperty(
      '--primary-3',
      this.primary3Dark
    );
    document.documentElement.style.setProperty(
      '--secondary',
      this.secondaryDark
    );
    document.documentElement.style.setProperty(
      '--accent-1',
      this.secondaryDark
    );
    document.documentElement.style.setProperty(
      '--accent-2',
      this.secondaryDark
    );
    this.themeChange.next('dark');
    this.theme = 'dark';
    this.write();
  }

  private read(): string | null {
    if (!this.lsAvailable) return null;
    return localStorage.getItem(this.THEMING_STORAGE_KEY);
  }

  private write() {
    if (!this.lsAvailable) return;
    localStorage.setItem(this.THEMING_STORAGE_KEY, this.mode);
  }
}
